import { get, noop } from 'lodash';
import { getPageFromPathname } from './getPageFromPathname';
import isDocumentLoading from '../utils/isDocumentLoading';

export const AnalyticsTypes = {
    TrackEvent: 0,
    ChangePageView: 1,
    SetDimension: 2,
    LinkDomains: 3,
    TrackPreviousMonolithEvent: 4,
};

export const SettingType = {
    CustomDimension: 0,
    ContentGroup: 1,
};

export const DimensionKeys = {
    AbTestIds: 1,
    Specialty: 2,
    Placemark: 3,
    Procedure: 4,
    InsuranceCarrier: 5,
    InsurancePlan: 6,
    Language: 7,
    Market: 8,
    Brand: 9,
    Professional: 10,
    Provider: 11,
    // 12 is unused
    UserLoginStatus: 13,
    UserType: 14,
    ZlogPageType: 15,
    PageTypeZocMon: 16,
    ProfessionalActiveStatus: 17,
    Product: 18,
    WhiteLabel: 22,
    ProviderID: 23,
    StrategicID: 24,
    PatientType: 25,
    BookingId: 26,
    UserExperienceTest: 27,
    SessionID: 19,
};

export const ContentGroupKeys = {
    ZlogPageType: 1,
    ZlogRollupBucket: 2,
    // PageCategory: 3, // currently not used anywhere in PWA, but does exist in GA
    ZlogContentType: 4,
};

export const DimensionValues = {};
Object.keys(DimensionKeys).forEach(key => {
    DimensionValues[DimensionKeys[key]] = key;
});

export const ContentValues = {};
Object.keys(ContentGroupKeys).forEach(key => {
    ContentValues[ContentGroupKeys[key]] = key;
});

export const InteractionLabels = {
    Click: 'click',
    Keyboard: 'keyboard',
    Swipe: 'swipe',
};

export const RedirectLabels = {
    Internal: 'internal-redirect',
    External: 'external-redirect',
    NoRedirect: null,
};

let domContentLoaded = false;

if (isDocumentLoading()) {
    // The GA tracking contentGroup information for booking flow gets set
    // as a callback on DOMContentLoaded, so we need to wait for that.
    document.addEventListener('DOMContentLoaded', () => {
        // This setTimeout(fn, 0) pushes the execution of the inner code to
        // the back of the javascript execution stack.  This way our code gets
        // executed after any other code on DOMContentLoaded.
        setTimeout(() => {
            domContentLoaded = true;
        }, 0);
    });
} else {
    domContentLoaded = true;
}

function prefixWithTags(action, category, tags) {
    const buildingPrefixes = [];

    if (category && category !== '') {
        buildingPrefixes.push(`[${category}]`);
    }

    if (tags && tags.length > 0) {
        buildingPrefixes.push(`[${tags}]`);
    }

    buildingPrefixes.push(action);

    return buildingPrefixes.join(' ');
}

async function getEventTrackingObject(data, store) {
    const state = store.getState();
    const pathname = get(state, 'routing.locationBeforeTransitions.pathname');
    const { category } = await getPageFromPathname(pathname);

    const listedCategory = data.category || category;
    const listedAction = prefixWithTags(data.action, data.category, data.tags) || '';

    return {
        hitType: 'event',
        eventCategory: listedCategory,
        eventAction: listedAction,
        eventLabel: data.label,
        eventValue: data.value,
    };
}

async function trackEventWithData(tracker, data, store) {
    const trackingObject = await getEventTrackingObject(data, store);
    tracker('send', trackingObject);
}

function determineSettingType(settingType) {
    switch (settingType) {
        case SettingType.ContentGroup:
            return 'contentGroup';
        case SettingType.CustomDimension:
            return 'dimension';
        default:
            throw new Error('Please specify a valid setting type');
    }
}

export function createAnalyticsLogger(ga) {
    const _gaEventQueue = [];
    let _gaCheckLoop;

    function checkingLoop(store) {
        const tracker = typeof window !== 'undefined' ? window.ga : undefined;

        if (!tracker || !domContentLoaded) {
            return;
        }

        clearInterval(_gaCheckLoop);
        _gaCheckLoop = undefined;

        _gaEventQueue.forEach(action => processAction(action, tracker, store));
    }

    function processAction(action, tracker, store) {
        switch (action.meta.analytics.type) {
            case AnalyticsTypes.LinkDomains:
                tracker('create', action.meta.analytics.token, 'auto', { allowLinker: true });
                tracker('require', 'linker');
                tracker('linker:autoLink', action.meta.analytics.domains);
                break;
            case AnalyticsTypes.TrackEvent:
            case AnalyticsTypes.TrackPreviousMonolithEvent:
                trackEventWithData(tracker, action.meta.analytics, store);
                break;
            case AnalyticsTypes.ChangePageView:
                tracker('set', 'page', action.meta.analytics.page);
                tracker('send', 'pageview');
                break;
            case AnalyticsTypes.SetDimension:
                tracker('set',
                    determineSettingType(action.meta.analytics.settingType)
                        + action.meta.analytics.index,
                    action.meta.analytics.value);
                break;
            default:
                throw new Error('Please specify a valid analytics type');
        }
    }

    return store => next => action => {
        ga = ga || (typeof window !== 'undefined' ? window.ga : noop);
        const result = next(action);

        if (action.meta) {
            if (action.meta.analytics) {
                if (!ga || !domContentLoaded) {
                    _gaEventQueue.push(action);

                    if (!_gaCheckLoop) {
                        _gaCheckLoop = setInterval(checkingLoop, 100, store);
                    }
                } else {
                    processAction(action, ga, store);
                }
            }
        }

        return result;
    };
}

const GA = {
    AnalyticsTypes,
    SettingType,
    DimensionKeys,
    DimensionValues,
};

const analyticsLogger = createAnalyticsLogger();
analyticsLogger.withTracker = createAnalyticsLogger;

export { analyticsLogger, GA };
